import { StrictMode, useEffect } from "react";

import * as Sentry from "@sentry/react";
import {
  getFingerprintAsync,
  ThemeProvider,
  TrackingContext,
  WebPerformanceMeasurement,
} from "@shapr3d/frontend-lib";
import { ModalProvider } from "@shapr3d/frontend-lib";
import { passthrough } from "msw";
import { createRoot } from "react-dom/client";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";

import { App } from "./components/App/App";
import ErrorPage from "./components/ErrorPage/ErrorPage";
import {
  enableSentryForDevEnvs,
  Environment,
  environment,
  gtmUrl,
  ReCAPTCHA_URL,
  SENTRY_DSN,
  SentryEnvironment,
  sentryEnvironment,
} from "./config";
import DBAvailableProvider from "./contexts/DBAvailableProvider";
import { trackingAdapter } from "./lib/TrackingAdapter";
import { setupMockTeams } from "./mocks/users/utils";
import { version } from "./version";

getFingerprintAsync();

const networkErrorStrings = [
  "Load failed",
  "Failed to fetch",
  "Failed to load",
  "NetworkError when attempting to fetch resource.",
];

const expectedErrorPatterns = [
  ...networkErrorStrings,
  `Failed to load script: ${ReCAPTCHA_URL}`,
  "There was an error fetching the embed code from Vimeo.", // Expected oembed failure, sent as info not ignored
];
const expectedServiceErrors = ["NOT_FOUND", "UNAUTHORIZED"];

// Payment errors: downgraded to info so they don't trigger alerts/replays but remain queryable
const paymentErrorPatterns = [
  /active_payment_attempt/, // Chargebee payment object rejection on checkout
];

const ignoredErrorPatterns = [
  "The operation is insecure.",
  "Failed to read the 'localStorage' property from 'Window': Access is denied for this document.",
  /Non-Error promise rejection captured with value: Timeout \(.\)/, //Recaptcha errors: https://github.com/getsentry/sentry-javascript/issues/2514
  /pinterest/i, // Pinterest tracking pixel errors (no user impact)
  /reCAPTCHA Timeout/, // reCAPTCHA timeout variants (a), (b), etc.
  /Invalid call to runtime\.sendMessage\(\)\. Tab not found\./, // Browser extension errors, not our code
  /Error invoking enableDidUserTypeOnKeyboardLogging/, // Android WebView keyboard bridge, not actionable from web
  /is not embeddable/, // Vimeo video not configured for embedding, not a code bug
  /^Failed to fetch/, // extends exact "Failed to fetch" to also catch "Failed to fetch (url)" variants
];
const ignoredErrorStrings = [
  ...networkErrorStrings, // Ignore unprefixed network errors
  `Failed to load script: ${gtmUrl}`,
  "jb", // Unknown error that -probably- coming from the pinterest tracking pixel (no user impact)
  "Captcha error: Cannot read properties of undefined (reading 'reset')",
  "Non-Error promise rejection captured with value: Timeout",
];

// Errors matching Sentry inbound filters — still sent to Sentry but replays should not be captured
const replayIgnoredErrorPatterns = [
  /N\+1 API Call/,
  /vimeo\.com\/api\/oembed\.json/,
  /psb\.taboola\.com/,
  /Large HTTP payload/,
  /Failed to read a named property 'Element' from 'Window'/,
  /Error invoking postEvent: Method not found/,
  /Error invoking post: Method not found/,
  /Load failed \(prod\.api\.shapr3d\.com\)/,
  /Failed to execute 'insertBefore' on 'No/,
  /feature named `pageObserver` was not found/,
  /Failed to load script: https:\/\/js\.chargebee\.com\/v2\/chargebee\.js/,
  /^Load failed/, // extends exact "Load failed" to also catch "Load failed (url)" variants
  /Action timed out for \w+/, // Chargebee REPLY_TIMEOUT errors (registerMaster, registerField, loadJsInfo, …)
  /Event `CustomEvent` \(type=unhandledrejection\) captured as promise rejection/, // Chargebee unhandled rejection on checkout
];

const transformExpectedErrorEvent = (event: Sentry.ErrorEvent) => {
  if (event.level === "info") return event;

  const exception = event.exception?.values?.[event.exception.values.length - 1];
  if (exception) {
    const errorMessage = exception.value;
    if (errorMessage) {
      if (expectedErrorPatterns.some((filter) => errorMessage.endsWith(filter))) {
        event.level = "info";
      }
      if (
        exception.type === "ServiceError" &&
        expectedServiceErrors.some((filter) => errorMessage.endsWith(filter))
      ) {
        event.level = "info";
      }
      if (paymentErrorPatterns.some((pattern) => pattern.test(errorMessage))) {
        event.level = "info";
      }
    }
  }

  return event;
};

const shouldFilterErrorEvent = (event: Sentry.ErrorEvent) =>
  Boolean(event?.exception?.values?.every((error) => error.type === "AbortError"));

(async () => {
  console.info("App version:", version);
  if (environment !== Environment.PROD) {
    console.info("Environment:", environment);
    console.info("Sentry environment:", sentryEnvironment);
  }

  if (import.meta.env.VITE_APP_ENV === "local") {
    const { mockUserList, mockTeamsList, permanentMockTeams } = await import("./mocks/users");

    if (!import.meta.env.VITEST_WORKER_ID && !mockTeamsList().length) {
      setupMockTeams(...permanentMockTeams);
      console.group("Mock users:");
      console.table(mockUserList().map((u) => u.email));
      console.groupEnd();
    }

    const { worker } = await import("./mocks/browser");
    await worker.start({
      // This is to filter out all the analytics and telemetry stuff to avoid spamming the console
      onUnhandledRequest(req, print) {
        const url = new URL(req.url);

        if (
          url.host.includes("ingest.sentry.io") ||
          url.host.includes("js.chargebee.com") ||
          url.host.includes("wistia.com") ||
          url.host.includes("litix.io") ||
          url.pathname.includes("/favicon.ico") ||
          url.pathname.includes("/favicon.png") ||
          url.pathname.includes("/fonts") ||
          url.pathname.includes("/maintenance.json") ||
          url.pathname.includes("/static") ||
          url.pathname.includes("/src") ||
          url.pathname.includes("ads/ga-audiences") ||
          url.pathname.includes("hot-update.js") ||
          url.protocol.includes("chrome-extension")
        ) {
          return;
        }

        // GTM is disabled in LoadGTMLayout for local environments
        if (url.host.includes("googletagmanager") || url.host.includes("gtm-")) {
          return passthrough();
        }

        // Paddle Billing checkout loads its JS from CDN
        if (url.host.includes("cdn.paddle.com")) {
          return passthrough();
        }

        if (url.host.includes("stage.telemetry.api.shapr3d.com")) {
          (async () => {
            const telemetryRequest = (await req.json()) as { event_name: string }[];
            console.groupCollapsed(
              `Telemetry events:`,
              telemetryRequest.map((e) => e.event_name).join(", ")
            );
            console.table(telemetryRequest, ["event_name"]);
            console.groupEnd();
          })();

          return;
        }

        print.warning();
      },
    });
  }

  const isDevSentry = sentryEnvironment === SentryEnvironment.DEV;
  if (enableSentryForDevEnvs || !isDevSentry) {
    Sentry.init({
      dsn: SENTRY_DSN,
      debug: isDevSentry,
      integrations: [
        ...(isDevSentry
          ? []
          : [
              Sentry.thirdPartyErrorFilterIntegration({
                filterKeys: ["shapr3d-web-app"],
                behaviour: "drop-error-if-exclusively-contains-third-party-frames",
              }),
            ]),
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
        Sentry.replayIntegration({
          unmask: ["h1", "h2", "button"],
          maskAllText: true,
          blockAllMedia: false,
          maskAllInputs: true,
          beforeErrorSampling: (event) => {
            if (event.level === "info") return false;
            const messages = event.exception?.values?.map((v) => v.value ?? "") ?? [];
            return !messages.some(
              (msg) =>
                replayIgnoredErrorPatterns.some((pattern) => pattern.test(msg)) ||
                expectedErrorPatterns.some((filter) => msg.endsWith(filter)) ||
                paymentErrorPatterns.some((pattern) => pattern.test(msg))
            );
          },
          networkDetailAllowUrls: ["shapr3d.com"],
        }),
      ],
      environment: sentryEnvironment,
      // So we collect IP addresses
      sendDefaultPii: true,

      tracesSampler: () => {
        const url = window.location.pathname;
        if (url.includes("/checkout") || url.includes("/login/app")) {
          return 1.0;
        }
        return 0.08;
      },

      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,
      denyUrls: [
        // Paddle always throws the following error on intialization
        // "TypeError: Cannot read property 'callback_data' of null"
        /cdn\.paddle\.com/i,
      ],
      ignoreErrors: [
        ...ignoredErrorPatterns,
        ...ignoredErrorStrings.map((pattern) => new RegExp(`^${pattern.replaceAll("?", "\\?")}$`)),
      ],
      beforeSend(event) {
        if (shouldFilterErrorEvent(event)) {
          return null;
        }

        return transformExpectedErrorEvent(event);
      },
    });

    Sentry.setContext("Lib versions", {
      "web-app": version,
      sentry: Sentry.SDK_VERSION,
    });
  }

  performance.mark("webapp:global:react-root-mounted");
  createRoot(document.getElementById("root") as HTMLElement).render(
    <StrictMode>
      <Sentry.ErrorBoundary
        fallback={<ErrorPage />}
        beforeCapture={(scope) => {
          scope.setTag("location", "provider");
        }}
      >
        <TrackingContext.Provider value={trackingAdapter}>
          <ThemeProvider>
            <DBAvailableProvider>
              <ModalProvider>
                <App />
              </ModalProvider>
            </DBAvailableProvider>
          </ThemeProvider>
        </TrackingContext.Provider>
      </Sentry.ErrorBoundary>
    </StrictMode>
  );
})();

if (localStorage.getItem("enableDevtools") === "true") {
  import("./devtools");
}

const measureObserver = new PerformanceObserver((list) => {
  const entries = list.getEntries() as PerformanceMeasure[];
  const forwardedEntries = entries.filter((entry) => entry.name.startsWith("webapp:"));
  const telemetryEvents = forwardedEntries.map((entry) => new WebPerformanceMeasurement(entry));
  if (telemetryEvents.length > 0) {
    trackingAdapter.trackWebPerformanceMeasurementEvents(telemetryEvents);
  }
});

measureObserver.observe({ type: "measure", buffered: true });
